/*
 * AnmerkungenCard.tsx
 *
 * Created by Dr. Maximillian Dornseif 2020
 * Copyright (c) 2020, 2022 Maximillian Dornseif
 */

import 'easymde/dist/easymde.min.css';

import { ActionButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { FehlerChecker, LadeChecker } from '@hudora/hd-react-components';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { assertIsString } from 'assertate-debug';
import { usePanel } from 'fluentui-hooks';
import gql from 'graphql-tag';
import Markdown from 'markdown-to-jsx';
import React, { Suspense } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from 'urql';

import { useAuditPanel } from '../hooks/useAuditPanel';

const SimpleMDE = React.lazy(() => import('react-simplemde-editor'));

const sx = { fontSize: 12 };

export const AnmerkungCard = (props: { forDesignator: string }) => {
  const [result] = useQuery({
    query: ANMERKUNG_QUERY,
    variables: { forDesignator: props.forDesignator },
    pause: !props.forDesignator,
  });
  const { data, fetching, error } = result;

  if (!props.forDesignator) {
    return null;
  }

  if (fetching || error) {
    return <LadeChecker loading={fetching} error={error} label="Lade Anmerkungen …" />;
  }
  return <AnmerkungenAppInner {...props} data={data} />;
};

 const ANMERKUNG_QUERY = gql`
  query AnmerkungQuery($forDesignator: ID!) {
    anmerkung: getEntityAnmerkung(forDesignator: $forDesignator) {
      id
      designator
      forDesignator
      text
      created_at
      updated_at
    }
  }
`;

const ANMERKUNG_MUTATION = gql`
  mutation AnmerkungMutation($nr: ID!, $input: EntityAnmerkungInput!) {
    entityAnmerkungUpdate(designator: $nr, input: $input) {
      id
      designator
      forDesignator
      text
      created_at
      updated_at
    }
  }
`;

const simpleMdeOptions = {
  // autofocus: true,
  spellChecker: false,
  status: false,
}; // as SimpleMDE.Options

const AnmerkungenAppInner = (props: { forDesignator: string; data: any }) => {
  const { auditPanel, auditButton } = useAuditPanel(
    props.data?.anmerkung?.designator,
    undefined,
    'Anmerkung'
  );
  const [openEditPanel, editPanelProps, , dismissEditPanel] = usePanel(`Anmerkung bearbeiten`);
  const [textValue, setTextValue] = React.useState('');
  const [mutationResult, mutateEntity] = useMutation(ANMERKUNG_MUTATION);
  const { fetching, error } = mutationResult;

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async () => {
    assertIsString(props.data?.anmerkung?.designator);
    const result = await mutateEntity({
      nr: props.data?.anmerkung?.designator,
      input: { text: textValue },
    });
    if (result !== undefined && !error) {
      dismissEditPanel();
      toast.success('Anmerkung wurde gespeichert!');
    }
  };

  // https://github.com/Ionaru/easy-markdown-editor#configuration
  const text = props?.data?.anmerkung?.text ?? '';

  return (
    <>
      <Card>
        <CardContent sx={sx}>
          <section title={`Anmerkung zu ${props.forDesignator}`}>
            <Typography color="text.secondary" gutterBottom>
              Anmerkung{' '}
              <ActionButton iconProps={{ iconName: 'Edit' }} ariaLabel="Edit" onClick={openEditPanel} />
              {auditButton}
              <br />
              {props.forDesignator}
            </Typography>
            <section aria-label="Anmerkung">{props.data ? <Markdown>{text}</Markdown> : null}</section>
          </section>
        </CardContent>
      </Card>
      <Panel {...editPanelProps} type={PanelType.medium}>
        <FehlerChecker error={error} />
        <Suspense fallback={<div>Lade Editor…</div>}>
          <SimpleMDE onChange={setTextValue} value={props.data?.anmerkung?.text} options={simpleMdeOptions} />
        </Suspense>
        <br />
        <PrimaryButton disabled={fetching} text="Speichern" onClick={uebertragen}>
          {fetching ? <Spinner labelPosition="right" size={SpinnerSize.small} /> : null}
        </PrimaryButton>
      </Panel>
      {auditPanel}
    </>
  );
};
